import React from 'react';
import { Button, Card, Text } from '@fragments';
import { IconRefresh, IconSparkles, IconMoodEmpty, IconFilterOff, IconUserPlus } from '@tabler/icons-react';
import { planText } from '../../components/PlanName';
import { STUDIO_HOME } from '../../enums/routes';
import TopicCard from './components/TopicCard';
import LockedTopicCard from './components/LockedTopicCard';
import TrendingToolbar from './components/TrendingToolbar';
import useTrending from './hooks/useTrending';
import './styles.scss';

const SKELETONS = [0, 1, 2, 3, 4, 5];

// /studio/trending — the Pro gallery of trending topics: search and categories over the
// ranked topics, each one offered to a format. Without Pro, blurred placeholders, each
// leading to the plan. Scrolls inside the shell, under the header's blur.
export default function Trending({ studio: s }) {
  const { t } = s;
  const tr = useTrending(s);
  const state = (icon, text, action) => <div className="studio-trending__state">
    <span className="studio-trending__state-icon" aria-hidden="true">{icon}</span>
    <Text.p contrast={7}>{text}</Text.p>
    {action}
  </div>;

  const body = tr.loading ? <div className="studio-trending__grid" aria-busy="true">
      {SKELETONS.map(n => <Card key={n} className="studio-trend-card" style={{ '--studio-trend-index': n }}>
        <Text.caption loading>{t('trending')}</Text.caption>
        <Text.h5 loading>{t('inspiration')}</Text.h5>
        <Text.p loading>{t('trendingSubtitle')}</Text.p>
      </Card>)}
    </div>
    : !tr.unlocked ? <div className="studio-trending__grid">
      {tr.samples.map((sample, index) => <LockedTopicCard key={sample.id} sample={sample} studio={s} index={index} onUnlock={tr.unlock} />)}
    </div>
    : tr.needsProfile ? state(<IconUserPlus />, t('trendingNeedsProfile'),
      <Button variant="rounded" color="inverse" onClick={() => s.navigate(STUDIO_HOME)}>{t('trendingConnectProfile')}</Button>)
    : tr.topics.length ? <div className="studio-trending__grid">
      {tr.topics.map((topic, index) => <TopicCard key={topic.id} topic={topic} studio={s} index={index} />)}
    </div>
    : tr.filtering ? state(<IconFilterOff />, t('trendingNoResults'),
      <Button variant="rounded" color="overlay" onClick={tr.clearFilters}>{t('trendingClearFilters')}</Button>)
    : state(<IconMoodEmpty />, t('noTopics'),
      <Button variant="rounded" color="overlay" startIcon={<IconRefresh />} loading={s.topicsState === 'loading'} onClick={s.refreshTopics}>{t('refresh')}</Button>);

  return <div className="studio-trending">
    <div className="studio-trending__inner">
      <header className="studio-trending__head">
        <div className="studio-trending__heading">
          <Text.caption contrast={6} left={<IconSparkles />}>{planText(t, 'trendingEyebrow', t('planName_pro'))}</Text.caption>
          <Text.h2 weight={400} contrast={9}>{t('inspiration')}</Text.h2>
          <Text.p contrast={7}>{t('trendingSubtitle')}</Text.p>
        </div>
        {tr.unlocked && !tr.needsProfile && <Button className="studio-trending__refresh" variant="rounded" color="overlay" icon={<IconRefresh />}
          aria-label={t('refresh')} tooltip={t('refresh')} loading={s.topicsState === 'loading'} onClick={s.refreshTopics} />}
      </header>
      <TrendingToolbar studio={s} trending={tr} />
      {body}
    </div>
  </div>;
}
